import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Play, Pause, ArrowLeft, Archive, Trash2, Plus } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Slider } from '../../components/ui/Slider';
import { Progress } from '../../components/ui/Progress';
import { Textarea } from '../../components/ui/Textarea';
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/Sheet';
import { ScrollArea } from '../../components/ui/ScrollArea';
import { Label } from '../../components/ui/Label';
import { freesoundSounds, freesoundCollections } from '../../src/seed';
export function FreesoundSoundDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const sound = freesoundSounds.find(s => s.id === id);
  const [isPlaying, setIsPlaying] = useState(false);
  if (!sound) {
    return <Card data-id="element-248">
        <CardHeader data-id="element-249">
          <CardTitle data-id="element-250">Sound not found</CardTitle>
        </CardHeader>
      </Card>;
  }
  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const soundCollections = freesoundCollections.filter(c => sound.collectionIds.includes(c.id));
  const otherCollections = freesoundCollections.filter(c => !sound.collectionIds.includes(c.id));
  const relatedSounds = freesoundSounds.filter(s => s.id !== sound.id && s.tags.some(t => sound.tags.includes(t))).slice(0, 5);
  return <div className="space-y-6 pb-10" data-id="element-251">
      <div data-id="element-252">
        <Button variant="ghost" size="sm" className="-ml-3 mb-2 text-muted-foreground" onClick={() => navigate(-1)} data-id="element-253">
          <ArrowLeft className="mr-2 h-4 w-4" data-id="element-254" />
          Back
        </Button>
        <div className="flex items-center gap-3" data-id="element-255">
          <Input defaultValue={sound.name} className="text-3xl font-bold tracking-tight h-auto py-1 px-2 -ml-2 border-transparent hover:border-input focus-visible:border-input bg-transparent" data-id="element-256" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-257">
        <div className="lg:col-span-2 space-y-6" data-id="element-258">
          <Card data-id="element-259">
            <CardContent className="p-6 space-y-6" data-id="element-260">
              <div className="h-32 bg-muted rounded-md flex items-center justify-center text-muted-foreground relative overflow-hidden" data-id="element-261">
                <div className="absolute inset-0 flex items-center px-2" data-id="element-262">
                  {Array.from({
                  length: 60
                }).map((_, i) => <div key={i} className="flex-1 mx-[1px] bg-primary/40 rounded-full" style={{
                  height: `${Math.max(10, Math.random() * 100)}%`
                }} data-id="element-263" />)}
                </div>
              </div>

              <div className="flex items-center gap-4" data-id="element-264">
                <Button size="icon" className="rounded-full h-12 w-12 shrink-0" onClick={() => setIsPlaying(!isPlaying)} data-id="element-265">
                  {isPlaying ? <Pause className="h-5 w-5" data-id="element-266" /> : <Play className="h-5 w-5 ml-1" data-id="element-267" />}
                </Button>
                <div className="flex-1 space-y-2" data-id="element-268">
                  <Progress value={isPlaying ? 35 : 0} className="h-2" data-id="element-269" />
                  <div className="flex justify-between text-xs text-muted-foreground font-medium" data-id="element-270">
                    <span data-id="element-271">{isPlaying ? '0:05' : '0:00'}</span>
                    <span data-id="element-272">{formatDuration(sound.duration)}</span>
                  </div>
                </div>
                <div className="w-24 shrink-0" data-id="element-273">
                  <Slider defaultValue={[80]} max={100} step={1} data-id="element-274" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-id="element-275">
            <CardHeader data-id="element-276">
              <CardTitle className="text-lg" data-id="element-277">Metadata</CardTitle>
            </CardHeader>
            <CardContent data-id="element-278">
              <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm" data-id="element-279">
                <div data-id="element-280">
                  <p className="text-muted-foreground mb-1" data-id="element-281">Duration</p>
                  <p className="font-medium" data-id="element-282">
                    {formatDuration(sound.duration)}
                  </p>
                </div>
                <div data-id="element-283">
                  <p className="text-muted-foreground mb-1" data-id="element-284">File type</p>
                  <p className="font-medium uppercase" data-id="element-285">{sound.type}</p>
                </div>
                <div data-id="element-286">
                  <p className="text-muted-foreground mb-1" data-id="element-287">Sample rate</p>
                  <p className="font-medium" data-id="element-288">44.1 kHz</p>
                </div>
                <div data-id="element-289">
                  <p className="text-muted-foreground mb-1" data-id="element-290">License</p>
                  <p className="font-medium" data-id="element-291">{sound.license}</p>
                </div>
                <div data-id="element-292">
                  <p className="text-muted-foreground mb-1" data-id="element-293">Created</p>
                  <p className="font-medium" data-id="element-294">Oct 12, 2023</p>
                </div>
                <div data-id="element-295">
                  <p className="text-muted-foreground mb-1" data-id="element-296">Creator</p>
                  <p className="font-medium" data-id="element-297">{sound.creator}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6" data-id="element-298">
          <Card data-id="element-299">
            <CardHeader data-id="element-300">
              <CardTitle className="text-lg" data-id="element-301">Edit details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-302">
              <div className="space-y-2" data-id="element-303">
                <Label data-id="element-304">Title</Label>
                <Input defaultValue={sound.name} data-id="element-305" />
              </div>
              <div className="space-y-2" data-id="element-306">
                <Label data-id="element-307">Category</Label>
                <Select defaultValue={sound.tags[0]} data-id="element-308">
                  <SelectTrigger data-id="element-309">
                    <SelectValue placeholder="Select category" data-id="element-310" />
                  </SelectTrigger>
                  <SelectContent data-id="element-311">
                    <SelectItem value="cardiac" data-id="element-312">Cardiac</SelectItem>
                    <SelectItem value="respiratory" data-id="element-313">Respiratory</SelectItem>
                    <SelectItem value="gi" data-id="element-314">Gastrointestinal</SelectItem>
                    <SelectItem value="equipment" data-id="element-315">Equipment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2" data-id="element-316">
                <Label data-id="element-317">Tags</Label>
                <div className="flex flex-wrap gap-2 mb-2" data-id="element-318">
                  {sound.tags.map(tag => <Badge key={tag} variant="secondary" className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors" data-id="element-319">
                      {tag} &times;
                    </Badge>)}
                </div>
                <div className="flex gap-2" data-id="element-320">
                  <Input placeholder="Add tag..." className="flex-1" data-id="element-321" />
                  <Button variant="secondary" data-id="element-322">Add</Button>
                </div>
              </div>
              <div className="space-y-2" data-id="element-323">
                <Label data-id="element-324">Description</Label>
                <Textarea placeholder="Add a description..." className="resize-none h-24" data-id="element-325" />
              </div>
              <Button className="w-full" data-id="element-326">Save changes</Button>
            </CardContent>
          </Card>

          <Card data-id="element-327">
            <CardHeader data-id="element-328">
              <CardTitle className="text-lg" data-id="element-329">Collections</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" data-id="element-330">
              <div className="flex flex-wrap gap-2" data-id="element-331">
                {soundCollections.map(c => <Link key={c.id} to={`/freesound/collections/${c.id}`} data-id="element-332">
                    <Badge variant="outline" className="hover:bg-muted cursor-pointer py-1 px-2" data-id="element-333">
                      {c.name}
                    </Badge>
                  </Link>)}
              </div>

              <Sheet data-id="element-334">
                <SheetTrigger asChild data-id="element-335">
                  <Button variant="ghost" className="w-full justify-start text-muted-foreground" data-id="element-336">
                    <Plus className="mr-2 h-4 w-4" data-id="element-337" />
                    Add to another collection
                  </Button>
                </SheetTrigger>
                <SheetContent data-id="element-338">
                  <SheetHeader className="mb-6" data-id="element-339">
                    <SheetTitle data-id="element-340">Add to collection</SheetTitle>
                    <SheetDescription data-id="element-341">
                      Select collections to add this sound to.
                    </SheetDescription>
                  </SheetHeader>
                  <div className="space-y-2" data-id="element-342">
                    {otherCollections.map(c => <div key={c.id} className="flex items-center justify-between p-3 border rounded-md hover:bg-muted/50 cursor-pointer" data-id="element-343">
                        <div data-id="element-344">
                          <p className="font-medium text-sm" data-id="element-345">{c.name}</p>
                          <p className="text-xs text-muted-foreground" data-id="element-346">
                            {c.itemCount} sounds
                          </p>
                        </div>
                        <Button size="sm" variant="secondary" data-id="element-347">
                          Add
                        </Button>
                      </div>)}
                    {otherCollections.length === 0 && <p className="text-sm text-muted-foreground text-center py-4" data-id="element-348">
                        No other collections available.
                      </p>}
                  </div>
                </SheetContent>
              </Sheet>
            </CardContent>
          </Card>
        </div>
      </div>

      {relatedSounds.length > 0 && <div className="space-y-4 pt-6" data-id="element-349">
          <h2 className="text-xl font-semibold tracking-tight" data-id="element-350">
            Related sounds
          </h2>
          <ScrollArea className="w-full whitespace-nowrap pb-4" data-id="element-351">
            <div className="flex w-max space-x-4" data-id="element-352">
              {relatedSounds.map(rs => <Card key={rs.id} className="w-[250px] shrink-0 cursor-pointer hover:shadow-md transition-all" onClick={() => navigate(`/freesound/library/${rs.id}`)} data-id="element-353">
                  <CardContent className="p-4 space-y-3" data-id="element-354">
                    <div className="flex items-center justify-between" data-id="element-355">
                      <Button variant="secondary" size="icon" className="h-8 w-8 rounded-full shrink-0" onClick={e => {
                  e.stopPropagation();
                }} data-id="element-356">
                        <Play className="h-3 w-3 ml-0.5" data-id="element-357" />
                      </Button>
                      <Badge variant="outline" className="text-[10px]" data-id="element-358">
                        {formatDuration(rs.duration)}
                      </Badge>
                    </div>
                    <div data-id="element-359">
                      <h4 className="font-medium text-sm truncate" data-id="element-360">
                        {rs.name}
                      </h4>
                      <p className="text-xs text-muted-foreground truncate" data-id="element-361">
                        by {rs.creator}
                      </p>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </ScrollArea>
        </div>}

      <Card className="border-muted bg-muted/20" data-id="element-362">
        <CardHeader data-id="element-363">
          <CardTitle className="text-lg text-foreground/80" data-id="element-364">
            Danger zone
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-4" data-id="element-365">
          <Button variant="outline" className="flex-1 sm:flex-none" data-id="element-366">
            <Archive className="mr-2 h-4 w-4" data-id="element-367" />
            Archive sound
          </Button>
          <div className="flex-1 flex flex-col sm:flex-row gap-4 sm:justify-end" data-id="element-368">
            <div className="text-sm text-muted-foreground sm:text-right flex items-center" data-id="element-369">
              This will permanently delete the sound from your library.
            </div>
            <Button variant="destructive" data-id="element-370">
              <Trash2 className="mr-2 h-4 w-4" data-id="element-371" />
              Delete permanently
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>;
}